import type { PixelSettings } from '../../config/types/pixel-settings';

const JVZOO_HCID_MAX_AGE_MS = 3 * 60 * 60 * 1000;

export type JvzooCheckoutIdentity = {
  workspaceId?: string;
  pixelId?: string;
};

/**
 * Configuração para o checkout hospedado da JVZoo.
 *
 * A identidade vem do `src` do script. Os dois identificadores são opcionais:
 * quando presentes, seguem juntos no payload; quando ausentes, não impedem o
 * envio dos eventos.
 */
export function createJvzooCheckoutSettings(
  identity: JvzooCheckoutIdentity = {}
): PixelSettings {
  return {
    identity: {
      ...(identity.workspaceId ? { workspaceId: identity.workspaceId } : {}),
      ...(identity.pixelId ? { pixelId: identity.pixelId } : {}),
    },
    app: {
      source: 'jvzoo',
      target: 'checkout',
      platform: 'jvzoo',
    },
    // O botão BUY NOW pode navegar para o próximo passo imediatamente.
    // Para não perder uma conversão no unload, cada evento deste preset é
    // encaminhado sem esperar a janela de batch padrão.
    transport: {
      batch: {
        enabled: true,
        batchSize: 1,
        maxWaitMs: 0,
      },
    },
    modules: {
      core: {
        pageView: { enabled: true },
        urlContext: {
          enabled: true,
          managedParams: [
            {
              key: 'public_session',
              // Equivalente ao subid4 do BuyGoods: preserva o identificador
              // que a plataforma já recebeu, sem alterar a URL do checkout.
              param: 'tid',
              mode: 'read_only',
              writeStrategy: 'none',
              persistence: { enabled: false },
            },
            {
              // ID de correlação do checkout: criado no primeiro acesso,
              // refletido na URL e reutilizado por até três horas.
              key: 'jvzoo_hcid',
              param: 'hcid',
              mode: 'read_write',
              writeStrategy: 'replaceState',
              generator: { type: 'public_id', prefix: 'hcid' },
              persistence: {
                enabled: true,
                strategy: 'localStorage_cookie',
                storageKey: 'jvzoo_hcid',
                maxAgeMs: JVZOO_HCID_MAX_AGE_MS,
                cookie: {
                  enabled: true,
                  name: 'jvzoo_hcid',
                  domain: 'auto',
                  path: '/',
                  sameSite: 'Lax',
                  secure: true,
                  maxAgeMs: JVZOO_HCID_MAX_AGE_MS,
                },
              },
            },
          ],
          linkDecoration: { enabled: false },
        },
      },
      conversion: {
        auto: {
          detectors: {
            removeFromCart: { enabled: false },
            purchase: { enabled: false },
            search: { enabled: false },
            viewContent: { enabled: false },
            // Mantido igual ao preset BuyGoods: checkout não emite LEAD.
            lead: { enabled: false },
            addToCart: {
              enabled: true,
              sources: [
                {
                  name: 'jvzoo-email-complete',
                  type: 'form',
                  fireOnce: true,
                  selectorForm: {
                    fields: ['email'],
                    mode: 'all',
                    // O checkout real da JVZoo não expõe um ID de formulário
                    // estável. Deixamos o detector resolver o form/contêiner
                    // nativo do próprio campo de e-mail.
                    // ATC é exclusivo da digitação no e-mail. Não escuta o
                    // click do BUY NOW, pois esse click pertence ao evento
                    // ADD_PAYMENT_INFO.
                    trigger: 'input',
                    debounceMs: 0,
                    fireOnce: true,
                  },
                },
              ],
            },
            // Mantido igual ao preset BuyGoods: checkout não emite INITIATE_CHECKOUT.
            initiateCheckout: { enabled: false },
            addPaymentInfo: {
              enabled: true,
              sources: [
                {
                  name: 'jvzoo-pay-button',
                  type: 'selector',
                  // A JVZoo pode reidratar o checkout e variar o ID do botão.
                  // Todos os seletores pertencem à mesma source: o fireOnce
                  // impede duplicidade mesmo se um botão casar com mais de um.
                  selector: [
                    '#whopPayButton',
                    '#submit',
                    '[data-track="add_payment_info"]',
                    'button[type="submit"]',
                    'input[type="submit"]',
                    { type: 'text', value: 'BUY NOW', exact: true },
                  ],
                  selectorType: 'css',
                  trigger: 'click',
                  fireOnce: true,
                },
              ],
            },
          },
        },
      },
      behavior: {
        enabled: true,
        heartbeat: { enabled: true },
      },
    },
  };
}
